import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface UserProfile {
  id: string;
  user_id: string;
  company_id: string | null;
  role: "manager" | "staff";
  created_at: string;
  has_seen_how_it_works: boolean;
}

interface Company {
  id: string;
  name: string;
  // Manager-only (fetched via secure RPC)
  join_code?: string | null;
  join_code_expires_at?: string | null;
  created_at: string;
  check_in_enabled?: boolean;
  gps_required?: boolean;
  check_in_radius_meters?: number | null;
  availability_due_weekday?: number | null;
  availability_due_time?: string | null;
  min_rest_hours_between_shifts?: number | null;
  max_consecutive_work_days?: number | null;
  // Payroll cycle settings
  payroll_cycle_type?: string | null;
  payroll_anchor_day?: number | null;
  payroll_period_end_rule?: string | null;
  timezone?: string | null;
  week_start_day?: number | null;
  // Dashboard settings
  dashboard_widgets_config?: Record<string, boolean> | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  company: Company | null;
  loading: boolean;
  needsSetup: boolean;
  signOut: () => Promise<void>;
  refetchUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  const fetchUserData = async (userId: string) => {
    try {
      // Fetch profile and role in parallel
      const [profileRes, roleRes] = await Promise.all([
        supabase
          .from("user_profiles")
          .select("*")
          .eq("user_id", userId)
          .maybeSingle(),
        supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId)
          .maybeSingle(),
      ]);

      if (profileRes.error) throw profileRes.error;
      const profileData = profileRes.data;
      
      // No profile exists - needs setup
      if (!profileData) {
        setProfile(null);
        setCompany(null);
        setNeedsSetup(true);
        return;
      }

      // Use role from user_roles table if available, otherwise fallback to profile
      const userRole = roleRes.data?.role || profileData.role;
      
      setProfile({ ...profileData, role: userRole } as UserProfile);

      // Profile exists but no company_id - needs setup
      if (!profileData.company_id) {
        setCompany(null);
        setNeedsSetup(true);
        return;
      }

      // Fetch company data (and join code for managers) in parallel
      const companyPromise = supabase
        .from("companies")
        .select(
          "id, name, created_at, check_in_enabled, gps_required, check_in_radius_meters, availability_due_weekday, availability_due_time, min_rest_hours_between_shifts, max_consecutive_work_days, dashboard_widgets_config"
        )
        .eq("id", profileData.company_id)
        .maybeSingle();

      const joinCodePromise = userRole === "manager"
        ? supabase.rpc("get_company_join_code", { p_company_id: profileData.company_id })
        : Promise.resolve({ data: null, error: null });

      const [companyRes, joinRes] = await Promise.all([companyPromise, joinCodePromise]);

      if (companyRes.error) throw companyRes.error;

      // Company not found - needs setup
      if (!companyRes.data) {
        setCompany(null);
        setNeedsSetup(true);
        return;
      }

      let join: { join_code: string | null; join_code_expires_at: string | null } | null = null;
      if (!joinRes.error && joinRes.data && Array.isArray(joinRes.data) && joinRes.data.length > 0) {
        join = {
          join_code: joinRes.data[0]?.join_code ?? null,
          join_code_expires_at: joinRes.data[0]?.join_code_expires_at ?? null,
        };
      }

      setCompany({
        ...(companyRes.data as Company),
        join_code: join?.join_code ?? null,
        join_code_expires_at: join?.join_code_expires_at ?? null,
      });
      setNeedsSetup(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const refetchUserData = async () => {
    if (user) {
      await fetchUserData(user.id);
    }
  };

  // Use ref to track if initial load is complete (accessible in closure)
  const initialLoadCompleteRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    // INITIAL load - controls loading state
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        // Fetch user data BEFORE setting loading false
        if (session?.user) {
          await fetchUserData(session.user.id);
        } else {
          // No session - not logged in, clear loading
          setProfile(null);
          setCompany(null);
          setNeedsSetup(false);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        if (isMounted) {
          initialLoadCompleteRef.current = true;
          setLoading(false);
        }
      }
    };

    // Set up auth state listener for ONGOING changes (not initial load)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      
      // Always update session/user state
      setSession(session);
      setUser(session?.user ?? null);

      // Skip if initial load hasn't completed yet - initializeAuth handles that
      if (!initialLoadCompleteRef.current) return;

      if (session?.user) {
        // Fire and forget for ongoing changes - don't control loading state
        fetchUserData(session.user.id).catch((error) => {
          console.error("Error fetching user data on auth change:", error);
        });
      } else {
        setProfile(null);
        setCompany(null);
        setNeedsSetup(false);
      }
    });

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setCompany(null);
    setNeedsSetup(false);
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, company, loading, needsSetup, signOut, refetchUserData }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
